import React from 'react';
import useAuth from '../../hooks/useAuth';
import './Dashboard.css';

const Dashboard = () => {
  const { hasPermission, hasRole } = useAuth();

  const CATEGORY_ORDER = ['Team & League Sales', 'Production Management', 'Admin & Vendors'];

  // Define available applications
  const apps = [
    {
      id: 'team-store-management',
      name: 'Online Team Store Management',
      description: 'Manage team stores, view orders, and generate reports',
      icon: '🏪',
      path: '/team-store-management',
      permission: 'team-stores:read',
      requiredRole: 'admin',
      external: false,
      category: 'Team & League Sales',
    },
    {
      id: 'crm',
      name: 'Team Sales CRM',
      description: 'Schools, programs, and season buying windows',
      icon: '🏫',
      path: '/crm',
      permission: 'crm:read',
      external: false,
      category: 'Team & League Sales',
    },
    {
      id: 'uniform-orders',
      name: 'League Uniform Management',
      description: 'Manage league uniform orders and inventory',
      icon: '🏃',
      path: '/league-uniform-management',
      permission: 'uniform-orders:read',
      requiredRole: 'admin',
      external: false,
      category: 'Team & League Sales',
    },
    {
      id: 'intake-queue',
      name: 'League Intake Queue',
      description: 'Review league uniform order requests from the portal',
      icon: '📬',
      path: '/intake-queue',
      permission: 'uniform-orders:read',
      requiredRole: 'admin',
      external: false,
      category: 'Team & League Sales',
    },
    {
      id: 'kerrscreen-production',
      name: 'Kerrscreen Production',
      description: 'Screen printing job tracking',
      icon: '🖨️',
      path: '/kerrscreen-production',
      permission: 'kerrscreen:read',
      external: false,
      category: 'Production Management',
    },
    {
      id: 'work-orders',
      name: 'Digital Work Orders',
      description: 'Kerr Screen work order management',
      icon: '📄',
      path: '/work-orders',
      permission: 'work-orders:read',
      requiredRole: 'admin',
      external: false,
      category: 'Production Management',
    },
    {
      id: 'file-sharing',
      name: 'File Sharing',
      description: 'File sharing and art file management',
      icon: '📁',
      path: '/file-sharing',
      permission: 'file-sharing:read',
      permissions: ['file-sharing:read', 'kerrscreen:read'],
      external: false,
      category: 'Production Management',
    },
    {
      id: 'spikes-gear',
      name: 'Spikes Gear',
      description: 'Custom t-shirt orders',
      icon: '👕',
      path: '/spikes-gear',
      permission: 'spikes-gear:read',
      external: false,
      category: 'Production Management',
    },
    {
      id: 'spirit-wear',
      name: 'Spirit Wear Inventory',
      description: 'Track spirit wear products and inventory by SKU',
      icon: '👕',
      path: '/spirit-wear',
      requiredRole: 'admin',
      external: false,
      category: 'Production Management',
    },
    {
      id: 'special-orders',
      name: 'Special Orders',
      description: 'Manage custom orders',
      icon: '📋',
      path: '/special-orders',
      permission: 'special-orders:read',
      external: false,
      category: 'Production Management',
    },
    {
      id: 'team-store-checkin-checkout',
      name: 'Team Store Checkin/Checkout',
      description: 'Scan store-pickup orders in and out',
      icon: '🏷️',
      path: '/team-store-checkin-checkout',
      external: false,
      category: 'Production Management',
    },
    {
      id: 'vendors',
      name: 'Vendor Portals',
      description: 'Vendor login credentials and B2B portals',
      icon: '🏢',
      path: '/vendors',
      requiredRole: 'admin',
      external: false,
      category: 'Admin & Vendors',
    },
    {
      id: 'careers',
      name: 'Job Applications',
      description: 'Review employment applications from the website',
      icon: '📝',
      path: '/careers',
      permission: 'careers:read',
      requiredRole: 'admin',
      external: false,
      category: 'Admin & Vendors',
    },
  ];

  const accessibleApps = apps.filter((app) => {
    // Check permission - support both single permission and permissions array (OR logic)
    if (app.permissions) {
      if (!app.permissions.some(p => hasPermission(p))) return false;
    } else if (app.permission && !hasPermission(app.permission)) {
      return false;
    }
    // Check required role if specified
    if (app.requiredRole && !hasRole(app.requiredRole)) return false;
    return true;
  });

  const groupedApps = CATEGORY_ORDER
    .map((category) => ({
      category,
      apps: accessibleApps.filter((app) => app.category === category),
    }))
    .filter((group) => group.apps.length > 0);

  return (
    <div className="dashboard-container">
      {groupedApps.length > 0 ? (
        groupedApps.map((group) => (
          <div key={group.category} className="apps-section">
            <h2 className="apps-section-title">{group.category}</h2>
            <div className="apps-grid">
              {group.apps.map((app) => (
                <a
                  key={app.id}
                  href={app.path}
                  target={app.external ? '_blank' : undefined}
                  rel={app.external ? 'noopener noreferrer' : undefined}
                  className="app-card"
                >
                  <div className="app-icon">{app.icon}</div>
                  <h3>{app.name}</h3>
                  <p>{app.description}</p>
                  <span className="app-link">Launch →</span>
                </a>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="no-access">
          <p>You don't have access to any applications yet.</p>
          <p>Please contact your administrator.</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
